import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
    Clock,
    CheckCircle2,
    AlertTriangle,
    Send,
    ChevronRight,
    Activity,
    Hourglass,
    Flag,
} from "lucide-react";
import { motion } from "framer-motion";

const STATUS_META = {
    pending: { label: "Pending", cls: "bg-gray-100 text-gray-700", icon: Clock },
    letter_sent: { label: "Letter Sent", cls: "bg-blue-100 text-blue-700", icon: Send },
    awaiting_response: { label: "Awaiting Response", cls: "bg-amber-100 text-amber-700", icon: Hourglass },
    resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
    partially_resolved: { label: "Partial Resolution", cls: "bg-teal-100 text-teal-700", icon: CheckCircle2 },
    denied: { label: "Denied", cls: "bg-rose-100 text-rose-700", icon: AlertTriangle },
    escalated: { label: "Escalated", cls: "bg-purple-100 text-purple-700", icon: Flag },
    litigation: { label: "Litigation", cls: "bg-red-100 text-red-800", icon: Flag },
};

const StatCard = ({ label, value, tone = "default", icon: Icon }) => {
    const toneCls = {
        default: "bg-muted/50",
        accent: "bg-accent/10 text-accent-foreground",
        warn: "bg-amber-50 text-amber-900",
        danger: "bg-rose-50 text-rose-900",
    }[tone];
    return (
        <div className={`rounded-xl p-3 flex items-center gap-3 ${toneCls}`}>
            {Icon && <Icon className="h-5 w-5 flex-shrink-0 opacity-70" />}
            <div>
                <p className="text-2xl font-bold leading-none font-mono" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
            </div>
        </div>
    );
};

const DisputeRow = ({ item, index }) => {
    const meta = STATUS_META[item.status] || STATUS_META.pending;
    const Icon = meta.icon;
    const overdue = item.is_active && item.days_remaining === 0;
    const urgent = item.is_active && item.days_remaining !== null && item.days_remaining <= 5 && !overdue;

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
            className={`rounded-lg border bg-background p-4 transition-colors ${
                overdue ? "border-rose-300" : urgent ? "border-amber-300" : "border-border hover:border-foreground/20"
            }`}
            data-testid={`timeline-item-${index}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`${meta.cls} border-0 gap-1`}>
                            <Icon className="h-3 w-3" />
                            {meta.label}
                        </Badge>
                        <span className="font-medium text-foreground truncate">{item.account_name || item.issue_type}</span>
                        <span className="text-xs text-muted-foreground">· {item.recipient}</span>
                    </div>

                    {item.is_active && item.days_elapsed !== null && (
                        <div className="flex items-center gap-2 text-xs">
                            <Clock className={`h-3.5 w-3.5 ${overdue ? "text-rose-600" : urgent ? "text-amber-600" : "text-muted-foreground"}`} />
                            <span className={overdue ? "text-rose-700 font-medium" : urgent ? "text-amber-700 font-medium" : "text-muted-foreground"}>
                                Day {item.days_elapsed} of FCRA {item.fcra_deadline_days}-day window ·{" "}
                                {overdue
                                    ? "BUREAU OVERDUE · escalate now"
                                    : `${item.days_remaining} days left`}
                            </span>
                        </div>
                    )}

                    {item.next_action && (
                        <p className="text-xs text-muted-foreground italic line-clamp-2">
                            Next: {item.next_action}
                        </p>
                    )}
                </div>

                <Link to="/disputes" className="flex-shrink-0">
                    <Button variant="ghost" size="sm" className="rounded-full" data-testid={`timeline-open-${index}`}>
                        Manage
                        <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                </Link>
            </div>
        </motion.div>
    );
};

const DisputeTimeline = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let ok = true;
        api.get("/dashboard/summary")
            .then(r => { if (ok) setData(r.data); })
            .catch(() => {})
            .finally(() => { if (ok) setLoading(false); });
        return () => { ok = false; };
    }, []);

    if (loading) {
        return (
            <Card className="border-border">
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                    Loading campaign status...
                </CardContent>
            </Card>
        );
    }

    if (!data || data.counts.total === 0) {
        return null;
    }

    const { counts, items } = data;

    return (
        <Card className="border-border" data-testid="dispute-timeline">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                        <Activity className="h-5 w-5 text-primary" />
                        Your Credit-Repair Campaign
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">Live status across every dispute and mailing.</p>
                </div>
                <Link to="/disputes">
                    <Button variant="outline" size="sm" className="rounded-full" data-testid="timeline-view-all">
                        View all
                        <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                </Link>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-3">
                    <StatCard label="Active" value={counts.active} tone="accent" icon={Hourglass} />
                    <StatCard label="Resolved" value={counts.resolved} tone="default" icon={CheckCircle2} />
                    <StatCard label="Overdue" value={counts.overdue} tone={counts.overdue > 0 ? "danger" : "default"} icon={AlertTriangle} />
                </div>

                {/* Disputes */}
                <div className="space-y-3">
                    {items.slice(0, 6).map((item, i) => (
                        <DisputeRow key={item.dispute_id} item={item} index={i} />
                    ))}
                </div>

                {items.length > 6 && (
                    <Link to="/disputes" className="block">
                        <Button variant="ghost" size="sm" className="w-full rounded-full">
                            + {items.length - 6} more · open full tracker
                        </Button>
                    </Link>
                )}
            </CardContent>
        </Card>
    );
};

export default DisputeTimeline;
